import React, { useState, useRef, useEffect } from 'react';
import { closeEliteBuyerModal } from '../../utils/functions';
import apiService from '../../services/api';

const EliteBuyerModal = () => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    preferred_grade: [],
    business_type: [],
    city: '',
    mobile: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isGradeDropdownOpen, setIsGradeDropdownOpen] = useState(false);
  const [isBusinessTypeDropdownOpen, setIsBusinessTypeDropdownOpen] = useState(false);
  const gradeDropdownRef = useRef(null);
  const businessTypeDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside grade dropdown
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target)) {
        setIsGradeDropdownOpen(false);
      }
      // Check if click is outside business type dropdown
      if (businessTypeDropdownRef.current && !businessTypeDropdownRef.current.contains(event.target)) {
        setIsBusinessTypeDropdownOpen(false);
      }
    };

    // Add event listener when any dropdown is open
    if (isGradeDropdownOpen || isBusinessTypeDropdownOpen) {
      // Use capture phase to catch clicks before they bubble
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isGradeDropdownOpen, isBusinessTypeDropdownOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Mobile number validation - only digits, max 10
    if (name === 'mobile') {
      const mobileValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: mobileValue
      });
      if (errors.mobile) {
        setErrors({ ...errors, mobile: '' });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleGradeToggle = (grade) => {
    setFormData(prev => {
      const isSelected = prev.preferred_grade.includes(grade);
      const newGrades = isSelected
        ? prev.preferred_grade.filter(g => g !== grade)
        : [...prev.preferred_grade, grade];
      
      return { ...prev, preferred_grade: newGrades };
    });
  };

  const handleBusinessTypeToggle = (type) => {
    setFormData(prev => {
      const isSelected = prev.business_type.includes(type);
      const newTypes = isSelected
        ? prev.business_type.filter(t => t !== type)
        : [...prev.business_type, type];
      
      return { ...prev, business_type: newTypes };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Mobile validation - exactly 10 digits
    if (!formData.mobile || !formData.mobile.trim()) {
      newErrors.mobile = 'Please enter your mobile number';
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }
    
    // Business Type validation - at least one required
    if (!formData.business_type || formData.business_type.length === 0) {
      newErrors.business_type = 'Please select at least one business type';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    const submitFormData = new FormData();
    submitFormData.append('name', formData.name);
    submitFormData.append('company_name', formData.company_name);
    submitFormData.append('city', formData.city);
    submitFormData.append('mobile', formData.mobile);
    submitFormData.append('email', formData.email);
    
    // Handle arrays for multiselect
    formData.preferred_grade.forEach(grade => {
      submitFormData.append('preferred_grade[]', grade);
    });
    formData.business_type.forEach(type => {
      submitFormData.append('business_type[]', type);
    });
    
    submitFormData.append('form_type', 'elite_buyer_club');

    try {
      const result = await apiService.submitEliteBuyer(submitFormData);
      if (result.success) {
        alert('Thank you! Your Elite Buyer Club application has been submitted successfully. Our team will contact you shortly.');
        setFormData({
          name: '',
          company_name: '',
          preferred_grade: [],
          business_type: [],
          city: '',
          mobile: '',
          email: ''
        });
        setErrors({});
        setIsGradeDropdownOpen(false);
        setIsBusinessTypeDropdownOpen(false);
        closeEliteBuyerModal();
      } else {
        alert(result.message || 'Error submitting form. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="elite-buyer-modal" className="modal" style={{ display: 'none' }} onClick={(e) => {
      // Close modal when clicking on backdrop
      if (e.target.id === 'elite-buyer-modal') {
        closeEliteBuyerModal();
      }
    }}>
      <div className="modal-content">
        <span className="close-modal" onClick={closeEliteBuyerModal}>&times;</span>
        <h2>Join Elite Buyer Club</h2>
        <p className="modal-subtitle">Fill out the form below to become a member of our exclusive Elite Buyer Club</p>
        <form id="elite-buyer-form" onSubmit={handleSubmit} className="elite-form-vertical">
          <input type="hidden" name="form_type" value="elite_buyer_club" />
          
          <div className="form-group">
            <label>Name <span className="required">*</span></label>
            <input 
              type="text" 
              name="name" 
              id="elite-name" 
              required 
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label>Company Name <span className="required">*</span></label>
            <input 
              type="text" 
              name="company_name" 
              id="elite-company" 
              required 
              placeholder="Enter your company name"
              value={formData.company_name}
              onChange={handleChange}
            />
          </div>
          
          <div className={`form-group ${isGradeDropdownOpen ? 'dropdown-open' : ''}`}>
            <label>Preferred Grade</label>
            <div className="custom-multi-select" ref={gradeDropdownRef}>
              <button
                type="button"
                className={`custom-select-trigger ${isGradeDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsGradeDropdownOpen(!isGradeDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isGradeDropdownOpen}
              >
                <span className="select-placeholder">
                  {formData.preferred_grade.length > 0 
                    ? `${formData.preferred_grade.length} grade${formData.preferred_grade.length > 1 ? 's' : ''} selected`
                    : 'Select Preferred Grade'
                  }
                </span>
                <span className="select-arrow">
                  <i className={`fas fa-chevron-${isGradeDropdownOpen ? 'up' : 'down'}`}></i>
                </span>
              </button>
              {isGradeDropdownOpen && (
                <div className="custom-select-dropdown" onClick={(e) => e.stopPropagation()}>
                  <div className="dropdown-list">
                    {['PVC', 'HDPE', 'LDPE', 'LLDPE', 'PP', 'PET', 'ABS', 'Taita', 'EVAPC', 'GPPS', 'HIPS', 'EPS', 'Nylon 6', 'Other'].map((grade, index) => (
                      <label 
                        key={index} 
                        className="dropdown-option"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={formData.preferred_grade.includes(grade)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleGradeToggle(grade);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="checkbox-label">{grade}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className={`form-group ${isBusinessTypeDropdownOpen ? 'dropdown-open' : ''}`}>
            <label>Business Type <span className="required">*</span></label>
            <div className="custom-multi-select" ref={businessTypeDropdownRef}>
              <button
                type="button"
                className={`custom-select-trigger ${isBusinessTypeDropdownOpen ? 'open' : ''} ${errors.business_type ? 'error' : ''} ${formData.business_type.length > 0 ? 'has-selection' : ''}`}
                onClick={() => setIsBusinessTypeDropdownOpen(!isBusinessTypeDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isBusinessTypeDropdownOpen}
              >
                <span className="select-placeholder">
                  {formData.business_type.length > 0 
                    ? `${formData.business_type.length} type${formData.business_type.length > 1 ? 's' : ''} selected`
                    : 'Select Business Type'
                  }
                </span>
                <span className="select-arrow">
                  <i className={`fas fa-chevron-${isBusinessTypeDropdownOpen ? 'up' : 'down'}`}></i>
                </span>
              </button>
              {isBusinessTypeDropdownOpen && (
                <div className="custom-select-dropdown" onClick={(e) => e.stopPropagation()}>
                  <div className="dropdown-list">
                    {['Importer', 'Manufacturer', 'Trader', 'Stockist'].map((type, index) => (
                      <label 
                        key={index} 
                        className="dropdown-option"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={formData.business_type.includes(type)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleBusinessTypeToggle(type);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="checkbox-label">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {errors.business_type && <span className="error-message">{errors.business_type}</span>}
          </div>
          
          <div className="form-group">
            <label>City <span className="required">*</span></label>
            <input 
              type="text" 
              name="city" 
              id="elite-city" 
              required 
              placeholder="e.g., Ahmedabad, Gujarat"
              value={formData.city}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label>Mobile No. <span className="required">*</span></label>
            <input 
              type="tel" 
              name="mobile" 
              id="elite-mobile" 
              required 
              pattern="[0-9]{10}" 
              placeholder="Enter 10-digit mobile number" 
              maxLength={10}
              inputMode="numeric"
              value={formData.mobile}
              onChange={handleChange}
            />
            {errors.mobile && <span className="error-message">{errors.mobile}</span>}
          </div>
          
          <div className="form-group">
            <label>Email Id <span className="required">*</span></label>
            <input 
              type="email" 
              name="email" 
              id="elite-email" 
              required 
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
          
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EliteBuyerModal;

